/**
 * Mother Milk Store
 *
 * Manages mother milk container data, filtering, searching, sorting, and CRUD operations.
 */
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import i18next from 'i18next';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import { logAuditEvent } from '@/core/services/audit.service';
import { computeDiff } from '@tms-phoenix/ui-vue';
import type {
  MotherMilkContainer,
  MilkReceivableMilkStatus,
  MilkReceivableMilkType,
  MotherMilkQueryParams,
  MotherMilkFilterState,
  BulkReceiveFailure,
} from '@/modules/customer-inventory/types';
import {
  fetchMotherMilkContainers,
  deleteMotherMilkContainer,
  createMotherMilkContainer,
  createMotherMilkContainersBulk,
  updateMotherMilkContainer,
  type CreateMotherMilkContainerPayload,
  type UpdateMotherMilkContainerPayload,
} from '@/modules/customer-inventory/services/mother-milk.api';

function motherMilkContainerToApiSnapshot(container: MotherMilkContainer): Record<string, unknown> {
  return {
    volume_ml: container.volumeMl,
    milk_type: container.milkType,
    milk_status: container.status,
    location: container.location,
    received_at: container.pumpedDate,
    expiry_at: container.expiryDate ?? null,
  };
}

export interface MotherMilkState {
  containers: MotherMilkContainer[];
  total: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  milkTypeFilter: MilkReceivableMilkType[];
  statusFilter: MilkReceivableMilkStatus[];
  sortField: 'pumpedDate' | 'expiryDate' | undefined;
  sortOrder: 'asc' | 'desc';
  /** Monotonically increasing counter — incremented on every fetch dispatch.
   *  Each in-flight request captures its generation at dispatch time; responses
   *  whose generation no longer matches the current value are discarded so
   *  stale loadNextPage results can never corrupt a fresher fetchContainers. */
  _fetchId: number;
}

export const useMotherMilkStore = defineStore('motherMilk', {
  state: (): MotherMilkState => ({
    containers: [],
    total: 0,
    currentPage: 1,
    pageSize: 25,
    isLoading: false,
    error: null,
    searchQuery: '',
    milkTypeFilter: [],
    statusFilter: [],
    sortField: undefined,
    sortOrder: 'desc',
    _fetchId: 0,
  }),

  getters: {
    hasContainers: (state) => state.containers.length > 0,
    totalPages: (state) => Math.ceil(state.total / state.pageSize),
    hasMore: (state) => state.containers.length < state.total,
  },

  actions: {
    /**
     * Fetch containers with current filters.
     */
    async fetchContainers(params?: Partial<MotherMilkQueryParams>): Promise<boolean> {
      this._fetchId += 1;
      const myId = this._fetchId;
      this.isLoading = true;
      this.error = null;

      try {
        const queryParams: MotherMilkQueryParams = {
          page: params?.page ?? this.currentPage,
          pageSize: params?.pageSize ?? this.pageSize,
          search: params?.search ?? this.searchQuery,
          milkType: params?.milkType ?? this.milkTypeFilter,
          status: params?.status ?? this.statusFilter,
          sortField: params?.sortField ?? this.sortField,
          sortOrder: params?.sortOrder ?? this.sortOrder,
        };

        const result = await fetchMotherMilkContainers(queryParams);
        if (this._fetchId !== myId) return true;
        this.containers = result.items;
        this.total = result.total;
        this.currentPage = queryParams.page ?? 1;
        return true;
      } catch (err: unknown) {
        if (this._fetchId !== myId) return false;
        this.error = getApiErrorMessage(err, 'Failed to fetch containers');
        return false;
      } finally {
        if (this._fetchId === myId) this.isLoading = false;
      }
    },

    /**
     * Update search query and refetch.
     */
    async setSearch(query: string): Promise<boolean> {
      this.searchQuery = query;
      this.currentPage = 1;
      return this.fetchContainers({ search: query, page: 1 });
    },

    /**
     * Reset the search query without triggering a fetch. Call this whenever
     * the view (re)mounts so a fresh visit to this module never inherits a
     * stale filter left over from a previous visit/session (the store is a
     * singleton and outlives route navigation, unlike component-local refs).
     */
    resetSearch() {
      this.searchQuery = '';
    },

    /**
     * Update filters (milk type and status) and refetch.
     */
    async setFilters(filters: MotherMilkFilterState): Promise<boolean> {
      this.milkTypeFilter = filters.milkType;
      this.statusFilter = filters.status;
      this.currentPage = 1;
      return this.fetchContainers({
        milkType: this.milkTypeFilter,
        status: this.statusFilter,
        page: 1,
      });
    },

    /**
     * Load the next page and append results (infinite scroll).
     */
    async loadNextPage(): Promise<boolean> {
      if (this.isLoading || this.containers.length >= this.total) return false;
      this._fetchId += 1;
      const myId = this._fetchId;
      this.isLoading = true;
      this.error = null;
      const nextPage = this.currentPage + 1;
      try {
        const queryParams: MotherMilkQueryParams = {
          page: nextPage,
          pageSize: this.pageSize,
          search: this.searchQuery,
          milkType: this.milkTypeFilter,
          status: this.statusFilter,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
        };
        const result = await fetchMotherMilkContainers(queryParams);
        if (this._fetchId !== myId) return false;
        this.containers = [...this.containers, ...result.items];
        this.total = result.total;
        this.currentPage = nextPage;
        return true;
      } catch (err: unknown) {
        if (this._fetchId !== myId) return false;
        this.error = getApiErrorMessage(err, 'Failed to fetch containers');
        return false;
      } finally {
        if (this._fetchId === myId) this.isLoading = false;
      }
    },

    /**
     * Delete a container by ID.
     */
    async removeContainer(id: string, reason?: string): Promise<boolean> {
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existingContainer = this.containers.find((c) => c.id === id);
      const auditOldValue = existingContainer
        ? motherMilkContainerToApiSnapshot(existingContainer)
        : undefined;

      try {
        const ok = await deleteMotherMilkContainer(id, reason);
        if (ok) {
          this.containers = this.containers.filter((c) => c.id !== id);
          this.total = Math.max(0, this.total - 1);
        }
        auditStatus = ok ? 'SUCCESS' : 'FAILURE';
        if (!ok) {
          auditErrorCode = 'DELETE_MOTHER_MILK_FAILED';
          auditErrorMessage = 'Container deletion returned false';
        }
        return ok;
      } catch (err: unknown) {
        this.error = err instanceof Error ? err.message : i18next.t('milk.container.deleteFailed');
        auditErrorCode = 'DELETE_MOTHER_MILK_FAILED';
        auditErrorMessage = this.error ?? 'Failed to delete container';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'DELETE_MOTHER_MILK_FAILED';
        }
        return false;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'INVENTORY',
            eventType: 'DELETE_MOTHER_MILK',
            eventStatus: auditStatus,
            resourceType: 'milk-receivables',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Create a new container and refetch page 1 so the new item appears
     * at the top of the list relative to the current sort/filter/search state.
     */
    async addContainer(payload: CreateMotherMilkContainerPayload): Promise<boolean> {
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const created = await createMotherMilkContainer(payload);
        auditStatus = 'SUCCESS';
        auditResourceId = created.id;
        // Refetch page 1 so the new item appears in the correct position
        // relative to the current sort/filter/search state, and the total
        // comes from the server rather than being incremented locally.
        return this.fetchContainers({ page: 1 });
      } catch (err: unknown) {
        this.error = getApiErrorMessage(err, 'mother.container.createFailed');
        auditErrorCode = 'CREATE_MOTHER_MILK_FAILED';
        auditErrorMessage = this.error ?? 'Failed to create container';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'CREATE_MOTHER_MILK_FAILED';
        }
        return false;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'INVENTORY',
            eventType: 'RECEIVE_MOTHER_MILK',
            eventStatus: auditStatus,
            resourceType: 'milk-receivables',
            resourceId: auditResourceId,
            isPhiAccess: false,
            newValue: auditStatus === 'SUCCESS' ? {
              receipt_number: payload.containerId,
              volume_ml: payload.volumeMl,
              milk_type: payload.milkType,
              milk_status: payload.status,
              location: payload.location,
              received_at: payload.pumpedDate,
              expiry_at: payload.expiryDate,
            } : undefined,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Create multiple containers in a single API call then refresh page 1.
     * Returns the number of successfully created containers.
     */
    async addContainersBulk(payloads: CreateMotherMilkContainerPayload[]): Promise<{ successCount: number; failed: BulkReceiveFailure[] }> {
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const { containers, failed } = await createMotherMilkContainersBulk(payloads);
        auditStatus = 'SUCCESS';
        await this.fetchContainers({ page: 1 });
        return { successCount: containers.length, failed };
      } catch (err: unknown) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedCreateMilkReceivable');
        auditErrorCode = 'CREATE_MOTHER_MILK_BULK_FAILED';
        auditErrorMessage = this.error ?? 'Failed to create containers';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'CREATE_MOTHER_MILK_BULK_FAILED';
        }
        return { successCount: 0, failed: [] };
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'INVENTORY',
            eventType: 'RECEIVE_MOTHER_MILK',
            eventStatus: auditStatus,
            resourceType: 'milk-receivables',
            isPhiAccess: false,
            newValue: auditStatus === 'SUCCESS' ? { count: payloads.length } : undefined,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Update an existing container by ID.
     * Refetches the current page so sort, filter, and search stay consistent
     * if the updated values no longer match the active criteria.
     */
    async updateContainer(id: string, payload: UpdateMotherMilkContainerPayload): Promise<boolean> {
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      const existingContainer = this.containers.find((c) => c.id === id);
      const updateApiPayload = {
        volume_ml: payload.volumeMl,
        milk_type: payload.milkType,
        milk_status: payload.status,
        location: payload.location,
        received_at: payload.pumpedDate,
        expiry_at: payload.expiryDate ?? null,
      };
      if (existingContainer) {
        const diff = computeDiff(motherMilkContainerToApiSnapshot(existingContainer), updateApiPayload);
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      } else {
        auditNewValue = updateApiPayload;
      }

      try {
        const updated = await updateMotherMilkContainer(id, payload);
        if (updated) {
          auditStatus = 'SUCCESS';
          return this.fetchContainers({ page: this.currentPage });
        }
        auditErrorCode = 'UPDATE_MOTHER_MILK_FAILED';
        auditErrorMessage = 'Update returned no data';
        return false;
      } catch (err: unknown) {
        this.error = err instanceof Error ? err.message : i18next.t('mother.container.updateFailed');
        auditErrorCode = 'UPDATE_MOTHER_MILK_FAILED';
        auditErrorMessage = this.error ?? 'Failed to update container';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'UPDATE_MOTHER_MILK_FAILED';
        }
        return false;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'INVENTORY',
            eventType: 'UPDATE_MOTHER_MILK',
            eventStatus: auditStatus,
            resourceType: 'milk-receivables',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },
  },
});
